import React, { Fragment, useState, useMemo } from 'react';
import EditIcon from '@material-ui/icons/Edit';
import EditOffIcon from '@material-ui/icons/LabelOff';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import TableHeaders, { DataCell, Icons, RowWrapper, SingleRow } from '../../Components/TableWithFilters/TableHeaders';
import { RootState } from '../../redux/root';
import InspectorDropdown from './InspectorDropdown';

const InspectionsTable = ({ rows, isFetching, onCellClick, openEditor }) => {
  const showAllPendingInspections = useSelector((s: RootState) => s.inspections.showAllPendingInspections);

  const [sortConfig, setSortConfig] = useState({ key: '', direction: 'asc' });

  const handleSort = columnName => {
    if (!columnName) return;
    setSortConfig(prev => ({
      key: columnName,
      direction: prev.key === columnName && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const sortedRows = useMemo(() => {
    if (!sortConfig.key) return rows;
    return [...rows].sort((a, b) => {
      const valA = a[sortConfig.key] ?? '';
      const valB = b[sortConfig.key] ?? '';
      return sortConfig.direction === 'asc'
        ? String(valA).localeCompare(String(valB))
        : String(valB).localeCompare(String(valA));
    });
  }, [rows, sortConfig]);

  const headersAll = [
    { label: 'Project Name|Permit Name', name: 'projectNamePermitName' },
    { label: 'Permit Number', name: 'permitNumber' },
    { label: 'Project Address', name: 'projectAddress' },
    { label: 'Inspection ID', name: 'id' },
    { label: 'Inspection Type', name: 'inspectionType' },
    { label: 'Project Manager', name: 'projectManager' },
    { label: 'Jurisdiction', name: 'jurisdiction' },
    { label: 'Inspector', name: 'inspectorName' },
    { label: 'Contact', name: 'contact' },
    { label: 'Cell Phone', name: 'cellPhone' },
    { label: 'Contact Phone', name: 'contactPhone' },
    { label: 'Actions', name: '' },
  ];

  const edit = row => {
    if (showAllPendingInspections) return;
    openEditor(row);
  };

  return (
    <PendingInspectionsWrapper>
      {isFetching && <Backdrop />}
      <TableHeaders
        headers={headersAll}
        rows={sortedRows}
        isLoading={isFetching}
        pristine={isFetching}
        onSort={handleSort}
        sortKey={sortConfig.key}
        sortDirection={sortConfig.direction}
      >
        {sortedRows &&
          sortedRows.map((row, index) => (
            <Fragment key={index}>
              <RowWrapper key={index} index={index}>
                <SingleRow2 count={headersAll.length} isEmergency={row.isEmergency}>
                  <DataCell>
                    <ProjectPermitLink onClick={() => onCellClick(row)} title={`${row.projectName || ''}${row.permitName && row.permitName !== row.projectName ? ` — ${row.permitName}` : ''}`}>
                      {row.projectName && <ProjectLine>{row.projectName}</ProjectLine>}
                      {row.permitName && row.permitName !== row.projectName && (
                        <PermitLine>{row.permitName}</PermitLine>
                      )}
                    </ProjectPermitLink>
                  </DataCell>
                  <DataCell>{row.permitNumber}</DataCell>
                  <DataCell>{row.projectAddress}</DataCell>
                  <DataCell>{row.id}</DataCell>
                  <DataCell>{row.inspectionType}</DataCell>
                  <DataCell>{row.projectManager}</DataCell>
                  <DataCell>{row.jurisdiction}</DataCell>
                  <DataCell>
                    {!showAllPendingInspections && <InspectorDropdown row={row} />}
                    {showAllPendingInspections && <span>{row.inspectorName}</span>}
                  </DataCell>
                  <DataCell>{row.contact}</DataCell>
                  <DataCell>{row.cellPhone}</DataCell>
                  <DataCell>{row.contactPhone}</DataCell>
                  <IconsEdit disabled={showAllPendingInspections}>
                    {!showAllPendingInspections && <EditIcon onClick={() => edit(row)} />}
                    {showAllPendingInspections && <EditOffIcon />}
                  </IconsEdit>
                  <InspectionDate>
                    <Label>Inspection Date:</Label>
                    <div>{row.inspectionDate}</div>
                  </InspectionDate>
                  <Information>
                    <Label>Information:</Label>
                    <div>{row.information}</div>
                  </Information>
                  <LastComment>
                    <Label>Comment:</Label>
                    <CommentWrapper>
                      {row.lastComment && <div dangerouslySetInnerHTML={{ __html: row.lastComment.text }} />}
                    </CommentWrapper>
                  </LastComment>
                </SingleRow2>
              </RowWrapper>
            </Fragment>
          ))}
      </TableHeaders>
    </PendingInspectionsWrapper>
  );
};

export default InspectionsTable;

const PendingInspectionsWrapper = styled.div`
  position: relative;
`;

const Backdrop = styled.div`
  position: absolute;
  background-color: rgba(0, 0, 0, 0.3);
  width: 200%;
  height: 100%;
  z-index: 3;
`;

const Label = styled.div`
  font-weight: bold;
`;

const SingleRow2 = styled(SingleRow)`
  overflow: hidden;
`;

const IconsEdit = styled(Icons)<{ disabled }>`
  cursor: ${({ disabled }) => (disabled ? 'inherit' : 'pointer')};
  color: ${({ disabled }) => (disabled ? 'grey' : 'inherit')};
`;

const CommentWrapper = styled.div`
  max-width: 370px;
  max-height: 130px;
  overflow: auto;
`;

const LastComment = styled.div`
  display: flex;
  gap: 6px;
  padding: 6px 16px 10px;
  grid-column: 1 / -1;
  background: transparent;
  font-size: 0.8125rem;
  color: var(--mainText);
`;

const Information = styled.div`
  display: flex;
  gap: 6px;
  padding: 6px 16px;
  grid-column: 1 / -1;
  background: transparent;
  font-size: 0.8125rem;
  color: var(--mainText);
`;

const InspectionDate = styled.div`
  display: flex;
  gap: 6px;
  padding: 6px 16px;
  grid-column: 1 / -1;
  background: transparent;
  font-size: 0.8125rem;
  color: var(--mainText);
`;

const ProjectPermitLink = styled.button`
  display: flex;
  flex-direction: column;
  gap: 2px;
  align-items: flex-start;
  padding: 0;
  background: transparent;
  border: 0;
  text-align: left;
  cursor: pointer;
  min-width: 0;
  width: 100%;
  font-family: inherit;

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
    border-radius: 4px;
  }
`;

const ProjectLine = styled.span`
  color: var(--primary-700);
  font-weight: 600;
  font-size: 0.875rem;
  line-height: 1.3;
  text-decoration: underline;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;

  ${ProjectPermitLink}:hover & {
    color: var(--primary-800, #1e6695);
  }
`;

const PermitLine = styled.span`
  color: var(--neutral-600);
  font-weight: 500;
  font-size: 0.75rem;
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
`;
